import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReducersTypes } from "@/redux/reducers/Models";
import { searchAddress, setCustomer } from "@/redux/ducks/customer";
import { Props } from "../Models/address";
import { getIsValidString } from "@/utils/functions";
import { NavigateFunction } from "react-router-dom";

const pinScheme = z.object({
  zipcode: z
    .string()
    .regex(/^\d{5}-\d{3}$/, "Cep inválido")
    .refine((val) => val.length === 9, "Cep inválido"),
  streetName: z.string().min(1, "Logradouro obrigatório"),
  number: z.string().min(1, "Número obrigatório"),
  complement: z.string().optional(),
  neighborhood: z.string().min(1, "Bairro obrigatório"),
  city: z.string().min(1, "Cidade obrigatória"),
  state: z.string().min(2, "Estado obrigatório"),
});

type State = {
  modalOpen: boolean;
  loading: boolean;
  editable: boolean;
};

export default function useAddressForm(navigate: NavigateFunction) {
  const dispatch = useDispatch();
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);

  const [ui, setUi] = useState<State>({
    modalOpen: false,
    loading: false,
    editable: false,
  });

  const unifiedSchema = pinScheme;
  type FormData = z.infer<typeof unifiedSchema>;

  const address = customer?.register?.address;

  const defaultValues: FormData = {
    zipcode: address?.zipcode ?? "",
    streetName: address?.streetName ?? "",
    number: address?.number ?? "",
    complement: address?.complement ?? "",
    neighborhood: address?.neighborhood ?? "",
    city: address?.city ?? "",
    state: address?.state ?? "",
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    clearErrors,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(unifiedSchema),
    mode: "onChange",
    defaultValues,
  });

  const zipcode = watch("zipcode");

  const toggleModal = useCallback(() => {
    setUi((prev) => ({ ...prev, modalOpen: !prev.modalOpen }));
  }, []);

  const onSubmit = useCallback(
    (data: FormData) => {
      if (ui.loading) return;
      dispatch(
        setCustomer({
          ...customer,
          register: {
            ...customer?.register,
            address: {
              ...customer?.register?.address,
              zipcode: data.zipcode,
              streetName: data.streetName,
              neighborhood: data.neighborhood,
              city: data.city,
              stateName: data.state,
              number: data.number,
              complement: data.complement,
            },
          },
        }),
      );
      navigate("/register/vehicle");
      reset();
    },
    [ui.loading, dispatch, customer, navigate, reset],
  );

  const handleSearchAddress = useCallback(
    (success: boolean, address?: Props["address"]) => {
      setUi((prev) => ({ ...prev, loading: false }));

      dispatch(
        setCustomer({
          ...customer,
          register: {
            ...customer?.register,
            address: {
              ...customer?.register?.address,
              zipcode: zipcode,
              stateId: address?.codigoEstado,
              neighborId: address?.codigoBairro,
              cityId: address?.codigoCidade,
              state: address?.uf,
              streetName: address?.endereco,
              neighborhood: address?.bairro,
              city: address?.cidade,
              stateName: address?.estado,
            },
          },
        }),
      );
      setValue("streetName", address?.endereco ?? "");
      setValue("city", address?.cidade ?? "");
      setValue("state", address?.uf ?? "");
      setValue("neighborhood", address?.bairro ?? "", { shouldValidate: true });

      if (!success || !getIsValidString(address?.estado)) {
        setUi((prev) => ({ ...prev, editable: true }));
        return;
      }

      setUi((prev) => ({
        ...prev,
        editable: !getIsValidString(address?.endereco),
      }));
      clearErrors();
    },
    [dispatch, customer, clearErrors, setValue, zipcode],
  );

  useEffect(() => {
    if (zipcode && zipcode.length === 9) {
      setUi((prev) => ({ ...prev, loading: true }));
      dispatch(
        searchAddress({
          zipcode,
          callback: handleSearchAddress,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipcode, dispatch]);

  return {
    toggleModal,
    isVisibleModal: ui.modalOpen,
    isLoading: ui.loading,
    isDisabled: !isValid || ui.loading,
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    editable: ui.editable,
  };
}
